import React, { useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../api";
import { getApiHeaders } from "../../utils/helpers";
import MessageToster from "../MessageToster";

export default function JoinLearnAndEarn({
  pointsPerUsd = 500,
  minRedeemPoints = 20000,
  onJoin,
  isJoining: propIsJoining = false,
}) {
  const [internalJoining, setInternalJoining] = useState(false);
  const isJoining = propIsJoining || internalJoining;

  const handleJoinClick = async () => {
    if (onJoin) {
      onJoin();
      return;
    }
    setInternalJoining(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/account/learn_earn_enroll`,
        {},
        { headers: getApiHeaders() }
      );
      if (
        (res?.data?.code === 200 || res?.data?.status === "success" || res?.data?.status === true) &&
        res?.data?.code !== 400
      ) {
        MessageToster(
          "success",
          res.data?.message || res.data?.msg || "Successfully enrolled in Learn & Earn!"
        );
      } else {
        MessageToster(
          "error",
          res?.data?.message || res?.data?.msg || "Failed to join program"
        );
      }
    } catch (err) {
      console.error("Error enrolling in Learn & Earn:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Something went wrong while enrolling in the program.";
      MessageToster("error", errMsg);
    } finally {
      setInternalJoining(false);
    }
  };
  const pillBadges = [
    { icon: "bx bx-help-circle", label: "Daily Quiz Challenges" },
    { icon: "bx bx-star", label: "Instant Point Rewards" },
    { icon: "bx bx-transfer-alt", label: "Convert Points to USD" },
    { icon: "bx bx-wallet", label: "Full-Amount Withdrawals" },
  ];

  return (
    <div className="join-learn-earn-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h2 className="fw-700 mb-0">Learn &amp; Earn</h2>
          </div>
        </div>
      </div>

      <div
        className="card  border-0 overflow-hidden mb-4 bg-primary-subtle rounded-4 position-relative shadow-sm"
        style={{
          border: "1px solid var(--bs-primary-border-subtle)",
        }}
      >
        <div className="card-body">
          <div className="row align-items-center g-3">
            <div className="col-12 col-md-8 col-lg-9">
              <div className="mb-3">
                <span
                  className="d-inline-flex align-items-center text-muted gap-1 px-3 py-1 font-size-11 fw-semibold text-uppercase"
                  style={{
                    backgroundColor: "var(--bg-primary)",
                    border: "1px solid var(--bs-primary-border-subtle)",
                    borderRadius: "var(--bs-border-radius-pill)",
                    letterSpacing: "0.5px",
                  }}
                >
                  <span>Exclusive Learning Rewards</span>
                </span>
              </div>
              <h2
                className="fw-bold mb-2"
                style={{
                  color: "var(--bs-emphasis-color)",
                  fontSize: "calc(1rem + 0.6vw)",
                  letterSpacing: "-0.5px",
                }}
              >
                Answer Questions Daily, Earn Real Cash
              </h2>
              <p
                className="mb-0 text-muted"
                style={{
                  fontSize: "14px",
                  lineHeight: "1.6",
                  maxWidth: "680px",
                }}
              >
                Join our Learn &amp; Earn program. Complete levels by answering daily quiz questions,
                accumulate points, and withdraw real USD directly to your account.
              </p>
            </div>

            <div className="col-12 col-md-4 col-lg-3 text-md-end">
              <button
                type="button"
                className="btn btn-primary btn-lg waves-effect waves-light d-inline-flex align-items-center justify-content-center px-4 py-2 font-size-14 fw-semibold shadow-sm"
                onClick={handleJoinClick}
                disabled={isJoining}
              >
                {isJoining ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2 text-white"
                      role="status"
                    />
                    Joining...
                  </>
                ) : (
                  <>
                    Join &amp; Start Earning
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="d-flex flex-nowrap align-items-center justify-content-start justify-content-md-center gap-2 gap-sm-3 mb-4 overflow-x-auto pb-2"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {pillBadges.map((badge, idx) => (
          <div
            key={idx}
            className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm font-size-12 fw-semibold flex-shrink-0 text-nowrap"
            style={{
              backgroundColor: "var(--bs-card-bg)",
              color: "var(--bs-emphasis-color)",
              border: "1px solid var(--bs-border-color)",
              whiteSpace: "nowrap",
            }}
          >
            <i
              className={`${badge.icon} font-size-16`}
              style={{ color: "var(--bs-primary)" }}
            ></i>
            <span>{badge.label}</span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes dashFlow {
          from {
            stroke-dashoffset: 24;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes verticalDashFlow {
          from {
            stroke-dashoffset: 20;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        .animated-dash-wave {
          stroke-dasharray: 6 4;
          animation: dashFlow 1.2s linear infinite;
        }
        .animated-vertical-dash {
          stroke-dasharray: 5 4;
          animation: verticalDashFlow 1.2s linear infinite;
        }
        .step-halo-circle {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .step-halo-circle:hover {
          transform: translateY(-4px) scale(1.06);
        }
      `}</style>

      <div className="mb-4">
        <h5
          className="fw-bold mb-1"
          style={{ color: "var(--bs-emphasis-color)", fontSize: "1.15rem" }}
        >
          How It Works
        </h5>
        <p className="mb-3 font-size-13 text-muted">
          Follow these 4 simple steps to start earning
        </p>

        <div
          className="card border shadow-sm rounded-4"
          style={{
            backgroundColor: "var(--bs-card-bg)",
            borderColor: "var(--bs-border-color)",
          }}
        >
          <div className="card-body p-4 p-md-5">
            <div
              className="d-none d-md-flex align-items-start justify-content-between flex-nowrap position-relative py-2 overflow-x-auto"
              style={{ minWidth: "780px" }}
            >
              {/* Step 1 */}
              <div className="text-center d-flex flex-column align-items-center flex-fill px-2">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-primary-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid var(--bs-primary-border-subtle)",
                    boxShadow: "0 0 24px rgba(var(--bs-primary-rgb), 0.22), 0 4px 12px rgba(var(--bs-primary-rgb), 0.12)",
                  }}
                >
                  <i className="bx bx-user-plus fs-2" style={{ color: "var(--bs-primary)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-3 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 1
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Join Program
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "12px", maxWidth: "165px", lineHeight: "1.45" }}
                >
                  Click the button above to enroll instantly
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center px-1 flex-shrink-0"
                style={{ height: "68px", width: "135px", marginTop: "52px" }}
              >
                <svg width="130" height="42" viewBox="0 0 130 42" fill="none" style={{ overflow: "visible" }}>
                  <defs>
                    <linearGradient id="themeWaveGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 0.2 }} />
                      <stop offset="60%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 0.75 }} />
                      <stop offset="100%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 8 23 C 36 7, 72 7, 98 17 C 107 20, 114 21, 118 21"
                    stroke="url(#themeWaveGrad1)"
                    style={{ stroke: "url(#themeWaveGrad1)" }}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    fill="none"
                    className="animated-dash-wave"
                  />
                  <path
                    d="M 115 15 L 126 21 L 115 27 Q 117 21 115 15 Z"
                    fill="var(--color-primary)"
                    style={{ fill: "var(--color-primary)" }}
                  />
                </svg>
              </div>

              {/* Step 2 */}
              <div className="text-center d-flex flex-column align-items-center flex-fill px-2">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-info-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid rgba(var(--bs-info-rgb), 0.3)",
                    boxShadow: "0 0 24px rgba(var(--bs-info-rgb), 0.22), 0 4px 12px rgba(var(--bs-info-rgb), 0.12)",
                  }}
                >
                  <i className="bx bx-help-circle fs-2" style={{ color: "var(--bs-info)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-3 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 2
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Answer Questions
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "12px", maxWidth: "165px", lineHeight: "1.45" }}
                >
                  Complete daily quizzes for each level
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center px-1 flex-shrink-0"
                style={{ height: "68px", width: "135px", marginTop: "52px" }}
              >
                <svg width="130" height="42" viewBox="0 0 130 42" fill="none" style={{ overflow: "visible" }}>
                  <defs>
                    <linearGradient id="themeWaveGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 0.2 }} />
                      <stop offset="60%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 0.75 }} />
                      <stop offset="100%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 8 23 C 36 7, 72 7, 98 17 C 107 20, 114 21, 118 21"
                    stroke="url(#themeWaveGrad2)"
                    style={{ stroke: "url(#themeWaveGrad2)" }}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    fill="none"
                    className="animated-dash-wave"
                  />
                  <path
                    d="M 115 15 L 126 21 L 115 27 Q 117 21 115 15 Z"
                    fill="var(--color-primary)"
                    style={{ fill: "var(--color-primary)" }}
                  />
                </svg>
              </div>

              <div className="text-center d-flex flex-column align-items-center flex-fill px-2">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-warning-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid rgba(var(--bs-warning-rgb), 0.3)",
                    boxShadow: "0 0 24px rgba(var(--bs-warning-rgb), 0.24), 0 4px 12px rgba(var(--bs-warning-rgb), 0.12)",
                  }}
                >
                  <i className="bx bxs-star fs-2" style={{ color: "var(--bs-warning)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-3 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 3
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Earn Points
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "12px", maxWidth: "165px", lineHeight: "1.45" }}
                >
                  Get points for every correct answer
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center px-1 flex-shrink-0"
                style={{ height: "68px", width: "135px", marginTop: "52px" }}
              >
                <svg width="130" height="42" viewBox="0 0 130 42" fill="none" style={{ overflow: "visible" }}>
                  <defs>
                    <linearGradient id="themeWaveGrad3" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 0.2 }} />
                      <stop offset="60%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 0.75 }} />
                      <stop offset="100%" stopColor="var(--color-primary)" style={{ stopColor: "var(--color-primary)", stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 8 23 C 36 7, 72 7, 98 17 C 107 20, 114 21, 118 21"
                    stroke="url(#themeWaveGrad3)"
                    style={{ stroke: "url(#themeWaveGrad3)" }}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    fill="none"
                    className="animated-dash-wave"
                  />
                  <path
                    d="M 115 15 L 126 21 L 115 27 Q 117 21 115 15 Z"
                    fill="var(--color-primary)"
                    style={{ fill: "var(--color-primary)" }}
                  />
                </svg>
              </div>

              <div className="text-center d-flex flex-column align-items-center flex-fill px-2">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-success-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid rgba(var(--bs-success-rgb), 0.3)",
                    boxShadow: "0 0 24px rgba(var(--bs-success-rgb), 0.24), 0 4px 12px rgba(var(--bs-success-rgb), 0.12)",
                  }}
                >
                  <i className="bx bx-credit-card fs-2" style={{ color: "var(--bs-success)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-3 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 4
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Withdraw Cash
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "12px", maxWidth: "165px", lineHeight: "1.45" }}
                >
                  Convert points to USD and withdraw 100%
                </div>
              </div>
            </div>

            <div className="d-flex d-md-none flex-column align-items-center py-2">
              <div className="text-center d-flex flex-column align-items-center w-100 px-3">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-primary-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid var(--bs-primary-border-subtle)",
                    boxShadow: "0 0 20px rgba(var(--bs-primary-rgb), 0.18)",
                  }}
                >
                  <i className="bx bx-user-plus fs-2" style={{ color: "var(--bs-primary)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-2 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 1
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Join Program
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "13px", maxWidth: "260px", lineHeight: "1.45" }}
                >
                  Click the button above to enroll instantly
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-center my-2">
                <svg width="24" height="38" viewBox="0 0 24 38" fill="none">
                  <path
                    d="M 12 3 L 12 28"
                    stroke="var(--color-primary)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    className="animated-vertical-dash"
                  />
                  <path
                    d="M 6 25 L 12 35 L 18 25 Q 12 27 6 25 Z"
                    fill="var(--color-primary)"
                  />
                </svg>
              </div>

              <div className="text-center d-flex flex-column align-items-center w-100 px-3">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-info-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid rgba(var(--bs-info-rgb), 0.3)",
                    boxShadow: "0 0 20px rgba(var(--bs-info-rgb), 0.18)",
                  }}
                >
                  <i className="bx bx-help-circle fs-2" style={{ color: "var(--bs-info)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-2 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 2
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Answer Questions
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "13px", maxWidth: "260px", lineHeight: "1.45" }}
                >
                  Complete daily quizzes for each level
                </div>
              </div>

              {/* Downward Connector 2 */}
              <div className="d-flex align-items-center justify-content-center my-2">
                <svg width="24" height="38" viewBox="0 0 24 38" fill="none">
                  <path
                    d="M 12 3 L 12 28"
                    stroke="var(--color-primary)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    className="animated-vertical-dash"
                  />
                  <path
                    d="M 6 25 L 12 35 L 18 25 Q 12 27 6 25 Z"
                    fill="var(--color-primary)"
                  />
                </svg>
              </div>

              {/* Step 3 */}
              <div className="text-center d-flex flex-column align-items-center w-100 px-3">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-warning-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid rgba(var(--bs-warning-rgb), 0.3)",
                    boxShadow: "0 0 20px rgba(var(--bs-warning-rgb), 0.18)",
                  }}
                >
                  <i className="bx bxs-star fs-2" style={{ color: "var(--bs-warning)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-2 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 3
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Earn Points
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "13px", maxWidth: "260px", lineHeight: "1.45" }}
                >
                  Get points for every correct answer
                </div>
              </div>

              {/* Downward Connector 3 */}
              <div className="d-flex align-items-center justify-content-center my-2">
                <svg width="24" height="38" viewBox="0 0 24 38" fill="none">
                  <path
                    d="M 12 3 L 12 28"
                    stroke="var(--color-primary)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    className="animated-vertical-dash"
                  />
                  <path
                    d="M 6 25 L 12 35 L 18 25 Q 12 27 6 25 Z"
                    fill="var(--color-primary)"
                  />
                </svg>
              </div>

              {/* Step 4 */}
              <div className="text-center d-flex flex-column align-items-center w-100 px-3">
                <div
                  className="step-halo-circle d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-success-subtle"
                  style={{
                    width: "68px",
                    height: "68px",
                    border: "1.5px solid rgba(var(--bs-success-rgb), 0.3)",
                    boxShadow: "0 0 20px rgba(var(--bs-success-rgb), 0.18)",
                  }}
                >
                  <i className="bx bx-credit-card fs-2" style={{ color: "var(--bs-success)" }}></i>
                </div>
                <div
                  className="fw-bold text-uppercase mt-2 mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.8px", color: "var(--color-secondary)" }}
                >
                  STEP 4
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "15px", color: "var(--bs-emphasis-color)" }}
                >
                  Withdraw Cash
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "13px", maxWidth: "260px", lineHeight: "1.45" }}
                >
                  Convert points to USD and withdraw 100%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h5
          className="fw-bold mb-1"
          style={{ color: "var(--bs-emphasis-color)", fontSize: "1.15rem" }}
        >
          Reward Rules
        </h5>
        <p className="mb-3 font-size-13 text-muted">
          Clear conversion and redemption criteria
        </p>

        <div className="row g-3">
          <div className="col-12 col-md-4">
            <div
              className="card border shadow-sm rounded-4 h-100 mb-0"
              style={{
                backgroundColor: "var(--bs-card-bg)",
                borderColor: "var(--bs-border-color)",
              }}
            >
              <div className="card-body p-4 d-flex align-items-center gap-3">
                <div className="avatar-sm flex-shrink-0">
                  <div
                    className="avatar-title bg-primary-subtle rounded-circle"
                    style={{ color: "var(--bs-primary)" }}
                  >
                    <i className="bx bx-check fs-4 fw-bold" style={{ color: "var(--bs-primary)" }}></i>
                  </div>
                </div>
                <div>
                  <div
                    className="text-muted fw-bold text-uppercase mb-1"
                    style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                  >
                    POINTS PER DOLLAR
                  </div>
                  <h5
                    className="fw-bold mb-1"
                    style={{ fontSize: "16px", color: "var(--bs-emphasis-color)" }}
                  >
                    {pointsPerUsd} Points = $1
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                    Earn {pointsPerUsd} points to get 1 USD
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div
              className="card  border shadow-sm rounded-4 h-100 mb-0"
              style={{
                backgroundColor: "var(--bs-card-bg)",
                borderColor: "var(--bs-border-color)",
              }}
            >
              <div className="card-body p-4 d-flex align-items-center gap-3">
                <div className="avatar-sm flex-shrink-0">
                  <div
                    className="avatar-title bg-warning-subtle rounded-circle"
                    style={{ color: "var(--bs-warning)" }}
                  >
                    <i className="bx bxs-star fs-4" style={{ color: "var(--bs-warning)" }}></i>
                  </div>
                </div>
                <div>
                  <div
                    className="text-muted fw-bold text-uppercase mb-1"
                    style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                  >
                    MINIMUM TO REDEEM
                  </div>
                  <h5
                    className="fw-bold mb-1"
                    style={{ fontSize: "16px", color: "var(--bs-emphasis-color)" }}
                  >
                    {Number(minRedeemPoints).toLocaleString()} Points
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                    Start redeeming from just {Number(minRedeemPoints).toLocaleString()} pts
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div
              className="card  border shadow-sm rounded-4 h-100 mb-0"
              style={{
                backgroundColor: "var(--bs-card-bg)",
                borderColor: "var(--bs-border-color)",
              }}
            >
              <div className="card-body p-4 d-flex align-items-center gap-3">
                <div className="avatar-sm flex-shrink-0">
                  <div
                    className="avatar-title bg-success-subtle rounded-circle"
                    style={{ color: "var(--bs-success)" }}
                  >
                    <i className="bx bx-check-shield fs-4" style={{ color: "var(--bs-success)" }}></i>
                  </div>
                </div>
                <div>
                  <div
                    className="text-muted fw-bold text-uppercase mb-1"
                    style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                  >
                    WITHDRAWAL
                  </div>
                  <h5
                    className="fw-bold mb-1"
                    style={{ fontSize: "16px", color: "var(--bs-emphasis-color)" }}
                  >
                    Paid in Full
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                    Full amount, no deductions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
